import React from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaCode } from 'react-icons/fa';

const Projects = () => {
  const projects = [
    {
      title: 'AI-Powered E-Commerce Platform',
      description:
        'Full-stack e-commerce application with intelligent product recommendations using RAG and LangChain. Features include real-time inventory management, secure payment integration, and advanced analytics dashboard.',
      tech: ['Next.js', 'TypeScript', 'MongoDB', 'LangChain', 'Pinecone', 'Stripe', 'Tailwind CSS'],
      features: [
        'AI-powered product recommendations',
        'Real-time inventory tracking',
        'Secure payment processing',
        'Admin analytics dashboard',
      ],
      gradient: 'var(--gradient-1)',
    },
    {
      title: 'Real-Time Collaboration Tool',
      description:
        'Enterprise-grade collaboration platform with real-time chat, video conferencing, and document sharing. Built with WebSocket technology for instant synchronization across all connected clients.',
      tech: ['React.js', 'Node.js', 'Socket.io', 'MongoDB', 'Redis', 'WebRTC', 'Material-UI'],
      features: [
        'Real-time messaging & notifications',
        'Video/audio conferencing',
        'Collaborative document editing',
        'Role-based access control',
      ],
      gradient: 'var(--gradient-2)',
    },
    {
      title: 'Healthcare Management System',
      description:
        'Comprehensive healthcare platform connecting patients, doctors, and administrators. Includes appointment scheduling, medical records management, telemedicine, and prescription tracking.',
      tech: ['Next.js', 'TypeScript', 'PostgreSQL', 'Prisma', 'Redux Toolkit', 'Chakra UI'],
      features: [
        'Appointment scheduling system',
        'Electronic health records',
        'Telemedicine integration',
        'Prescription management',
      ],
      gradient: 'var(--gradient-3)',
    },
    {
      title: 'Smart Finance Dashboard',
      description:
        'Advanced financial analytics platform with interactive charts, expense tracking, budget planning, and AI-powered insights for personal and business finance management.',
      tech: ['React.js', 'Node.js', 'MongoDB', 'Chart.js', 'Recharts', 'Express.js', 'JWT'],
      features: [
        'Real-time expense tracking',
        'Interactive data visualizations',
        'Budget forecasting with AI',
        'Multi-currency support',
      ],
      gradient: 'var(--gradient-1)',
    },
    {
      title: 'Social Media Analytics Platform',
      description:
        'Comprehensive social media analytics tool that aggregates data from multiple platforms, providing insights on engagement, growth metrics, and content performance with automated reporting.',
      tech: ['Next.js', 'TypeScript', 'MySQL', 'Redis', 'OAuth2', 'Tailwind CSS', 'Cron Jobs'],
      features: [
        'Multi-platform integration',
        'Advanced analytics & insights',
        'Automated report generation',
        'Competitor analysis',
      ],
      gradient: 'var(--gradient-2)',
    },
    {
      title: 'Learning Management System',
      description:
        'Complete LMS with course creation, student enrollment, progress tracking, assessments, and certification. Includes video streaming, interactive quizzes, and gamification elements.',
      tech: ['React.js', 'Node.js', 'MongoDB', 'Socket.io', 'AWS S3', 'Redux', 'Bootstrap'],
      features: [
        'Course creation & management',
        'Video streaming & storage',
        'Interactive assessments',
        'Progress tracking & certificates',
      ],
      gradient: 'var(--gradient-3)',
    },
  ];

  return (
    <section className="projects section" id="projects">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Featured Projects</h2>
          <p className="section-subtitle">Real-world applications built with modern technologies</p>
        </motion.div>

        <div className="projects-grid">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              className="project-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
            >
              <div className="project-gradient" style={{ background: project.gradient }}></div>

              <div className="project-content">
                <div className="project-header">
                  <FaCode className="project-icon" />
                  <h3>{project.title}</h3>
                </div>

                <p className="project-description">{project.description}</p>

                <div className="project-features">
                  <h4>Key Features:</h4>
                  <ul>
                    {project.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>

                <div className="project-tech">
                  {project.tech.map((tech, idx) => (
                    <span key={idx} className="tech-badge">
                      {tech}
                    </span>
                  ))}
                </div>

                <div className="project-links">
                  <a href="#" className="project-link">
                    <FaGithub /> View Code
                  </a>
                  <a href="#" className="project-link">
                    <FaExternalLinkAlt /> Live Demo
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;